import { useEffect, useRef } from "react";
import { useTheme } from "../../../contexts/ThemeContext";

export default function HeroSection() {
  const { isDark } = useTheme();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const t = setTimeout(() => el.classList.add("opacity-100"), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-black">
      {/* BG image — always dark for cinematic hero impact */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0cbf13592d011ec19d85352bcf437c89.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className={`absolute inset-0 bg-gradient-to-b ${isDark ? "from-black/55 via-black/45" : "from-black/65 via-black/55"} to-black`}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
      </div>

      {/* Ambient glows */}
      <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] rounded-full bg-[#F97316]/8 blur-[120px] pointer-events-none"></div>
      <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] rounded-full bg-[#22C55E]/6 blur-[100px] pointer-events-none"></div>

      {/* Content */}
      <div
        ref={contentRef}
        className="relative z-10 text-center flex flex-col items-center px-6 md:px-10 opacity-0 transition-opacity duration-1000 max-w-5xl mx-auto"
      >
        <div className="inline-flex items-center gap-2 border border-[#38BDF8]/35 rounded-full px-4 py-1.5 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse"></span>
          <span className="text-[#38BDF8] text-[11px] font-semibold tracking-[0.2em] uppercase">
            Workforce Infrastructure for Service Businesses
          </span>
        </div>

        <h1 className="text-[54px] md:text-[86px] font-black text-white leading-[1.02] tracking-[-0.03em] mb-7">
          Deploy Smarter Talent.<br />
          <span style={{
            background: "linear-gradient(135deg, #F97316 0%, #FBBF24 50%, #22C55E 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>
            Scale Without Hiring.
          </span>
        </h1>

        <p className="text-[17px] md:text-[19px] text-white/55 leading-[1.75] max-w-[600px] font-light mb-12">
          An AI-powered digital workforce that answers every call, converts every lead, and books every appointment — automatically, around the clock.
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={() => scrollTo("contact")}
            className="bg-[#F97316] text-white font-bold text-[15px] px-8 py-3.5 rounded-full hover:bg-[#FB923C] hover:scale-[1.03] transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Start Deployment
          </button>
          <button
            onClick={() => scrollTo("how-it-works")}
            className="flex items-center gap-2 text-white/65 hover:text-white font-medium text-[15px] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            See How It Works
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black to-transparent pt-20 pb-12 px-6 md:px-10">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: "< 30s", label: "Response Time", color: "#F97316" },
            { value: "3×", label: "Booking Rate", color: "#22C55E" },
            { value: "24/7", label: "Coverage", color: "#38BDF8" },
            { value: "0%", label: "Staff Turnover", color: "#F97316" },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="text-[28px] md:text-[36px] font-black leading-none tracking-tight" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="text-white/40 text-[12px] font-medium tracking-wider uppercase mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
